import React, { useEffect, useState } from 'react';
import { CardSection } from '../../shared/components/card/CardSection';
import {
  IconButton,
  InputBase,
  Paper,
  Box,
  Stack,
  Typography,
  Container,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { TableDefault } from '../../shared/components/table/TableDefault';
//import { useAuth } from '../auth/auth-hook';
import { TableColumnType } from '../../shared/components/table/table-types';
import moment from 'moment';
import { DateRangePickerWithText } from '../../shared/components/DateRangePicker';
import { formatterDateFilter } from '../../shared/utils';
import { DateFilter } from '../../shared/constants';
import { useBreakPoint } from '../../shared/hooks/useBreakPoint';
import { useNavigate } from 'react-router-dom';
import {
  useUsersLazyQuery,
  User,
  UserRoles,
  UserOrderInput,
  SortOrder,
  useToggleAdministratorUserRoleMutation,
  FilterSocialProviders,
  SubscriptionStatuses,
} from '../../shared/types/generated';
import { openSnackbarAction } from '../../shared/components/snackbar/snackbar-actions';
import { TypeMessage } from '../../shared/components/snackbar/snackbar-types';
import { TenantUserFilter, UserTableFilter } from './components/UserFilter';
import { useCallAction } from '@cobuildlab/react-simple-state';
import { cancelSubscriptionToAdmin } from './user-actions';
import { InputDefault } from '../../shared/components/inputs/InputDefault';
import { DialogDefault } from '../../shared/components/dialog/DialogDefault';

export const Users: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<TenantUserFilter | null>(null);
  const [total, setTotal] = useState<number>(0);
  const { isBelow } = useBreakPoint('sm');
  //const { user } = useAuth();
  const [{ from, to }, setDate] = useState<{ from?: string; to?: string }>({});
  const [{ page, pageSize }, setPagination] = useState({
    pageSize: 10,
    page: 1,
  });

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<UserOrderInput>({
    created_at: SortOrder.Desc,
  });
  const [userToDeleteSubs, setUserToDeleteSubs] = useState<User | null>(null);
  const [openDialogDeleteSubs, setOpenDialogDeleteSubs] = useState(false);
  const [reasonToDeleteSubs, setReasonToDeleteSubs] = useState('');

  const [callUsers, { data, loading, refetch }] = useUsersLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: (d) => setTotal(d?.users?.count || 0),
  });

  const [toogleAdministratorUserRole] = useToggleAdministratorUserRoleMutation({
    onCompleted: () => {
      refetch();
      openSnackbarAction(
        'El rol de administrador ha sido actualizado correctamente',
        TypeMessage.SUCCESS,
      );
    },
    onError: () => {
      openSnackbarAction(
        'Ha ocurrido un error al intentar actualizar el rol de administrador',
        TypeMessage.ERROR,
      );
    },
  });

  const [callCancelSubscription, loadingCancelSubs] = useCallAction(cancelSubscriptionToAdmin, {
    onCompleted() {
      refetch();
      setOpenDialogDeleteSubs(false);
      setReasonToDeleteSubs('');
      setUserToDeleteSubs(null);
      openSnackbarAction('La renovación se ha cancelado correctamente', TypeMessage.SUCCESS);
    },
    onError(error) {
      openSnackbarAction(error.message, TypeMessage.ERROR);
    },
  });

  const handleUserDetailsClick = (item: User): void => {
    navigate(`/users/${item.id}`);
  };

  useEffect(() => {
    callUsers({
      variables: {
        where: {
          created_at: {
            lte: to,
            gte: from,
          },
          ...(search !== '' && {
            created_at: undefined,
            OR: [
              {
                email: {
                  startsWith: search,
                },
              },
              {
                name: {
                  startsWith: search,
                },
              },
            ],
          }),
          /**...(filter &&
            filter?.subscribedUsers === true && {
              onlySubscribers: true,
            }), */
          ...(filter &&
            filter?.provider !== 'all' && {
              provider: filter.provider as FilterSocialProviders,
            }),
        },
        skip: (page - 1) * pageSize,
        take: pageSize,
        orderBy: sort,
      },
    });
  }, [page, pageSize, callUsers, from, to, search, sort, filter]);

  const COLUMNS: TableColumnType<User>[] = [
    {
      columnName: 'NOMBRE',
      columnValue: (item) => item.name || '',
      sortableField: 'name',
    },
    {
      columnName: 'EMAIL',
      columnValue: null,
      columnComponent: (item) => <Box>{item.email || ''}</Box>,
      sortableField: 'email',
    },
    {
      columnName: 'PROVEEDOR',
      columnValue: (item) => item.social_providers?.map((p) => p?.provider).join(',') || '',
    },
    {
      columnName: 'FECHA DE CREACIÓN',
      columnValue: (item) =>
        moment(parseInt(item.created_at || ''))
          .format('YYYY-MM-DD HH:mm'),
      sortableField: 'created_at',
    },
  ];

  const users = (data?.users?.items || []) as User[];

  return (
    <>
      <DialogDefault
        title="Cancelar subscripción"
        isOpen={openDialogDeleteSubs}
        handleClose={() => {
          setUserToDeleteSubs(null);
          setOpenDialogDeleteSubs(false);
        }}
        isLoading={loadingCancelSubs}
        disabledAction={!reasonToDeleteSubs || reasonToDeleteSubs.trim() === ''}
        handleSuccess={() => {
          if (userToDeleteSubs?.subscriptions && userToDeleteSubs?.subscriptions?.length > 0) {
            const subscription = userToDeleteSubs?.subscriptions?.find(
              (s) => s?.status === SubscriptionStatuses.Active,
            );
            if (subscription) {
              callCancelSubscription({
                reason: reasonToDeleteSubs,
                subscriptionId: subscription.id as number,
              });
            }
          }
        }}
        showActions
      >
        <Typography>
          ¿Estás seguro de cancelar la subscripción de <strong>{userToDeleteSubs?.name}</strong>?
        </Typography>
        <InputDefault
          value={reasonToDeleteSubs}
          placeholder="Por favor escriba el motivo"
          onChange={(e) => {
            setReasonToDeleteSubs(e.target.value);
          }}
          variant="outlined"
          sx={{ mt: 2.5 }}
          multiline
          minRows={3}
          maxRows={5}
        />
      </DialogDefault>
      {
        /**<CustomHeader
        title={`Bienvenido de vuelta, ${user?.name}`}
        subtitle={
          'Mide el IMPACTO de tus publicitaciones, registros, choques y tráfico del sitio web.'
        }
      /> */
      }
      <Container
        maxWidth={'lg'}
        style={{
          borderRadius: '16px',
          background: 'white',
          paddingTop: '16px',
          paddingBottom: '16px',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          width={'100%'}
          alignItems={'center'}
          justifyContent={{ xs: 'space-between' }}
        >
          <Typography fontSize={{ sx: '16px', md: '20px' }} fontWeight={600}>
            Usuarios
          </Typography>
          <Box sx={{ width: { sx: '100%', md: 'auto' } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              padding={'10px 0px'}
              spacing={1}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Paper
                sx={{
                  p: '2px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  maxWidth: 400,
                  height: '30px',
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder={isBelow ? 'BUSCAR' : 'BUSCAR USUARIO'}
                  inputProps={{ 'aria-label': 'search google maps' }}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                  }}
                  size="small"
                  onKeyUp={(e) => {
                    if (e.code === 'Enter') {
                      setPagination({
                        pageSize,
                        page: 1,
                      });
                    }
                  }}
                />

                <IconButton
                  type="button"
                  sx={{ p: '10px' }}
                  aria-label="search"
                  onClick={() => {
                    setPagination({
                      pageSize,
                      page: 1,
                    });
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Paper>
              <Box display="flex" alignItems="center" style={{ padding: '0px 0px 8px 0px' }}>
                <DateRangePickerWithText
                  onChange={(range) => {
                    const filters = formatterDateFilter(
                      range.startDate,
                      range.endDate,
                      range.key as DateFilter,
                    );

                    setDate({
                      from: filters.startDate,
                      to: filters.endDate,
                    });
                  }}
                />
                <UserTableFilter
                  onChangeFilter={setFilter}
                  provider={filter?.provider}
                  subscribedUsers={filter?.subscribedUsers}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
        <CardSection title={''}>
          <TableDefault
            items={users}
            count={total}
            columns={COLUMNS}
            page={page}
            pageSize={pageSize}
            loading={loading}
            messageLoading={'Cargando usuarios...'}
            messageEmpty='No se encontraron usuarios'
            handleSort={(field, order) => {
              setSort({
                [field]: order,
              });
            }}
            handleClickRow={(item) => handleUserDetailsClick(item)}
            handleChangePage={(p) => {
              setPagination({
                pageSize,
                page: p + 1,
              });
            }}
            showPagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            handleChangeRowPerPage={(e) => {
              setPagination({
                pageSize: parseInt(e.target.value, 10),
                page: 1,
              });
            }}
            actions={[
              {
                actionName: 'Ver detalles',
                onClick: (item) => handleUserDetailsClick(item),
              },
              {
                actionName: 'Eliminar subscripción',
                onClick: (item) => {
                  setUserToDeleteSubs(item);
                  setOpenDialogDeleteSubs(true);
                },
                isDisabled: (item) =>
                  !item?.subscriptions?.some((s) => s?.status === SubscriptionStatuses.Active),
              },
              {
                actionName: (item) => {
                  if (item.roles_on_users?.some((r) => r?.role?.name === UserRoles.Administrator)) {
                    return 'Quitar rol de administrador';
                  } else {
                    return 'Asignar rol de administrador';
                  }
                },
                onClick: (item) => {
                  toogleAdministratorUserRole({
                    variables: {
                      data: {
                        user_id: item.id,
                      },
                    },
                  });
                  setPagination({
                    pageSize,
                    page: 1,
                  });
                },
              },
            ]}
          />
        </CardSection>
      </Container>
    </>
  );
};
